import { Button, Divider, Flex, Popover, Typography } from "antd";
import styled from "styled-components";
import {
  CaretDownIcon,
  DotsSixVerticalIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CSSProperties, ReactNode, useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Chapter, SlideLayout, TemplateType } from "../../types";
import { useTemplateData } from "../../hooks/useTemplateData";
import { useTemplateEditorStore } from "../../store/useTemplateEditorStore";
import { useSlideMutations } from "../../hooks/useSlideMutations";
import { useSceneReorder } from "../../hooks/useSceneReorder";
import {
  getChapterHasTransition,
  getClosingEnabled,
  getIntroEnabled,
} from "../../helpers";
import { SlideTemplatePreview } from "../Template/SlideTemplatePreview";
import { TemplatePreview } from "../Template/TemplatePreview";
import type { AddableSlideLayout } from "../../helpers/buildDefaultSlide";
import { getSlotsForSlideTypeFromTheme } from "../../helpers/itemsHelper";
import { MainImageSelectModal } from "../MainImageSelectModal";
import {
  SortableSceneRow,
  StaticSceneRow,
  ToggleableSceneRow,
} from "./SceneRow";
import { Collapsible } from "./Collapsible";
import { restrictToVerticalAxis } from "./dndModifiers";
import { ENABLE_CLOSING_KEY, ENABLE_INTRO_KEY } from "../../constants";

const noop = () => undefined;

const SLIDES_I18N =
  "components.templates.TemplatesBoard.storyboardSlidesThumbnails";
const I18N = "components.templates.TemplatesBoard.storyboardLeftPanel";

const ADDABLE_LAYOUTS: AddableSlideLayout[] = [
  SlideLayout.BULLET_POINT,
  SlideLayout.QUOTE,
  SlideLayout.FIGURE,
  SlideLayout.IMAGE,
  SlideLayout.DOUBLE_PARAGRAPH,
  SlideLayout.PARAGRAPH_IMAGE,
];

const THUMBNAIL_BORDER = {
  borderWidth: 1,
  borderRadius: 0,
  borderColor: "#00000033",
};

const ChapterHeader = styled(Flex)`
  width: 100%;
  padding: 8px;
  cursor: pointer;
  user-select: none;

  /* Same hue as the Scenes/Items accordion headers (the right-panel tab-button
     tone) but more transparent, so chapters nest one level below them. */
  background-color: color-mix(
    in srgb,
    ${({ theme }) => theme.colorBgContainerDisabled} 50%,
    transparent
  );
  border-block-end: 1px solid ${({ theme }) => theme.colorBorderSecondary};
`;

const Caret = styled(CaretDownIcon)<{ $open: boolean }>`
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? 0 : -90)}deg);
  flex-shrink: 0;
`;

const DragHandle = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: grab;
  touch-action: none;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const COMPACT_THUMB_HEIGHT = 40;

const compactThumbSize = (aspectRatio: number | undefined) => {
  const ratio = aspectRatio ?? 9 / 16;
  return {
    height: COMPACT_THUMB_HEIGHT,
    width: Math.max(20, Math.round(COMPACT_THUMB_HEIGHT * ratio)),
  };
};

const ScenePickerPopover = ({
  onPick,
  children,
}: {
  onPick: (layout: AddableSlideLayout) => void;
  children: ReactNode;
}) => {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottom"
      title={t(`${SLIDES_I18N}.addScenePickerTitle`)}
      content={
        <Flex vertical gap={4}>
          {ADDABLE_LAYOUTS.map((layout) => (
            <Button
              key={layout}
              type="text"
              style={{ textAlign: "left", justifyContent: "flex-start" }}
              onClick={() => {
                setOpen(false);
                onPick(layout);
              }}
            >
              {t(`${SLIDES_I18N}.slideTypes.${layout}`)}
            </Button>
          ))}
        </Flex>
      }
    >
      {children}
    </Popover>
  );
};

type SortableChapterProps = {
  sectionId: string;
  index: number;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
};

const SortableChapter = ({
  sectionId,
  index,
  open,
  onToggle,
  children,
}: SortableChapterProps) => {
  const t = useTranslate();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    // Order persists via the storyboard cache (re-renders a tick after drop);
    // disabling the layout animation avoids the chapter animating back to its
    // original slot before the new order lands.
  } = useSortable({ id: sectionId, animateLayoutChanges: () => false });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Flex
      vertical
      ref={setNodeRef}
      style={{ ...style, width: "100%" }}
      {...attributes}
    >
      <ChapterHeader align="center" gap={8} onClick={onToggle}>
        <DragHandle {...listeners} onClick={(e) => e.stopPropagation()}>
          <DotsSixVerticalIcon size={20} weight="bold" />
        </DragHandle>
        <Typography.Text strong style={{ flex: 1, fontSize: 13 }}>
          {t(`${SLIDES_I18N}.chapter`, { number: index + 1 })}
        </Typography.Text>
        <Caret size={14} $open={open} />
      </ChapterHeader>
      <Collapsible open={open}>
        <Flex vertical style={{ width: "100%" }}>
          {children}
        </Flex>
      </Collapsible>
    </Flex>
  );
};

export const ScenesAccordion = ({
  handleSlideCardClick,
  handleChapterCardClick,
}: {
  handleSlideCardClick?: (uuid: string) => void;
  handleChapterCardClick?: (chapter: Chapter) => void;
}) => {
  const t = useTranslate();

  const {
    templatesData: { hasTransition, isFlashProject },
    brandKitData: { intro, closing },
    storyboardSections,
    projectTheme,
    mediaGenerationSettingsData: {
      mediaAspectRatio,
      mediaGenerationSettings,
      setChapterTransitionVisibility,
      setIntroClosingEnabled,
    },
  } = useTemplateData();

  const { currentSlide, currentChapter, slideNameToTemplateIdMap, templates } =
    useTemplateEditorStore((state) => ({
      currentSlide: state.currentSlide,
      currentChapter: state.currentChapter,
      slideNameToTemplateIdMap: state.slideNameToTemplateIdMap,
      templates: state.templates,
    }));

  const { addSlide, removeSlide } = useSlideMutations();
  const { reorderScenes, reorderChapters, contextHolder } = useSceneReorder();

  const [pendingPick, setPendingPick] = useState<{
    anchorUuid: string;
    layout: AddableSlideLayout;
  } | null>(null);

  // The chapter containing the current selection — the first chapter on initial
  // load (the default scene lives there), and the relevant chapter as
  // scroll-snap auto-selects a scene.
  const activeSectionId = isFlashProject
    ? storyboardSections.find((section) =>
        section.slides.some((slide) => slide.uuid === currentSlide)
      )?.id
    : currentChapter.sectionId || undefined;

  // The active chapter is always open (so scrolling to a scene opens its
  // chapter, even one the user previously closed); `openSections` only adds the
  // user's manual opens of other chapters. Derived, so no effect needed.
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const isOpen = (sectionId: string) =>
    sectionId === activeSectionId || !!openSections[sectionId];
  const toggle = (sectionId: string) =>
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !(sectionId === activeSectionId || !!prev[sectionId]),
    }));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const thumbSize = compactThumbSize(mediaAspectRatio);

  const layoutHasMainImage = (layout: AddableSlideLayout) => {
    if (!projectTheme) return false;
    const slots = getSlotsForSlideTypeFromTheme(projectTheme, layout);
    return !!slots?.some(
      (slot) => "mainImageItem" in slot && slot.mainImageItem
    );
  };

  const handlePick = (anchorUuid: string, layout: AddableSlideLayout) => {
    if (layoutHasMainImage(layout)) {
      setPendingPick({ anchorUuid, layout });
      return;
    }
    addSlide(anchorUuid, layout, "after").catch(() => undefined);
  };

  const handleChaptersDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = storyboardSections.map((s) => s.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    reorderChapters(arrayMove(ids, from, to)).catch(() => undefined);
  };

  const introChapter: Chapter = {
    templateId: mediaGenerationSettings?.introduction_template_id ?? undefined,
    topic: "Introduction",
    sectionId: "",
    type: TemplateType.INTRODUCTION,
  };
  const closingChapter: Chapter = {
    templateId: mediaGenerationSettings?.closing_template_id ?? undefined,
    topic: "Closing",
    sectionId: "",
    type: TemplateType.CLOSING,
  };

  const introEnabled = getIntroEnabled(mediaGenerationSettings, !!intro);
  const closingEnabled = getClosingEnabled(mediaGenerationSettings, !!closing);

  const sceneThumbnail = (chapter: Chapter) => (
    <TemplatePreview
      chapter={chapter}
      opacity={1}
      templateSize={thumbSize}
      border={THUMBNAIL_BORDER}
      onCardClick={() => handleChapterCardClick?.(chapter)}
    />
  );

  const renderStandaloneScene = (
    chapter: Chapter,
    label: string,
    selected: boolean
  ) => (
    <StaticSceneRow
      label={label}
      selected={selected}
      onSelect={() => handleChapterCardClick?.(chapter)}
      thumbnail={sceneThumbnail(chapter)}
    />
  );

  // Intro/closing stay visible (dimmed) when disabled, with an eye toggle.
  const renderToggleableScene = (
    chapter: Chapter,
    label: string,
    toggleLabel: string,
    enabled: boolean,
    selected: boolean,
    onToggle: (next: boolean) => void
  ) => (
    <ToggleableSceneRow
      label={label}
      toggleLabel={toggleLabel}
      enabled={enabled}
      selected={selected}
      onSelect={() => handleChapterCardClick?.(chapter)}
      onToggle={onToggle}
      thumbnail={sceneThumbnail(chapter)}
    />
  );

  return (
    <Flex vertical style={{ width: "100%" }}>
      {contextHolder}

      {intro &&
        // Intro/closing live outside the sortable chapter list. They stay
        // visible (dimmed) when disabled so users can re-enable them.
        renderToggleableScene(
          introChapter,
          t(`${SLIDES_I18N}.introScene`),
          introEnabled ? t(`${I18N}.disableIntro`) : t(`${I18N}.enableIntro`),
          introEnabled,
          currentChapter.type === TemplateType.INTRODUCTION,
          (enabled) => {
            setIntroClosingEnabled(ENABLE_INTRO_KEY, enabled).catch((e) =>
              console.error("Error toggling intro", e)
            );
          }
        )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleChaptersDragEnd}
      >
        <SortableContext
          items={storyboardSections.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          {storyboardSections.map((section, chapterIndex) => {
            const chapterSettings = mediaGenerationSettings?.chapters.find(
              (c) => c.chapter_id === section.id
            );
            const transitionTemplateId =
              chapterSettings?.transition_template_id ?? undefined;
            const chapterTemplateId =
              chapterSettings?.chapter_template_id ?? undefined;
            const chapterHasTransition = getChapterHasTransition(
              chapterSettings,
              hasTransition
            );

            const sceneSlides = isFlashProject
              ? section.slides.filter(
                  (s) =>
                    slideNameToTemplateIdMap[s.uuid] !== transitionTemplateId
                )
              : [];
            const canRemove = isFlashProject && sceneSlides.length > 1;
            const lastSceneUuid = sceneSlides.at(-1)?.uuid;

            const transitionChapter: Chapter = {
              templateId: transitionTemplateId,
              topic: section.topic,
              sectionId: section.id,
              type: TemplateType.TRANSITION,
            };
            const chapterChapter: Chapter = {
              templateId: chapterTemplateId,
              topic: section.topic,
              sectionId: section.id,
              type: TemplateType.CHAPTER,
            };

            // In Flash projects the transition is a slide, so selection is
            // driven by currentSlide; otherwise it's a chapter-type selection.
            const transitionSlide = isFlashProject
              ? section.slides.find(
                  (s) =>
                    slideNameToTemplateIdMap[s.uuid] === transitionTemplateId
                )
              : undefined;
            const transitionSlideTemplate = transitionSlide
              ? templates.get(transitionSlide.uuid)
              : undefined;
            const isTransitionSelected =
              isFlashProject && transitionSlide
                ? currentSlide === transitionSlide.uuid
                : currentChapter.type === TemplateType.TRANSITION &&
                  currentChapter.sectionId === section.id;

            return (
              <SortableChapter
                key={section.id}
                sectionId={section.id}
                index={chapterIndex}
                open={isOpen(section.id)}
                onToggle={() => toggle(section.id)}
              >
                <ToggleableSceneRow
                  label={t(`${SLIDES_I18N}.transitionScene`)}
                  toggleLabel={
                    chapterHasTransition
                      ? t(`${I18N}.disableTransition`)
                      : t(`${I18N}.enableTransition`)
                  }
                  selected={isTransitionSelected}
                  enabled={chapterHasTransition}
                  onSelect={() => {
                    if (isFlashProject && transitionSlide) {
                      handleSlideCardClick?.(transitionSlide.uuid);
                    } else {
                      handleChapterCardClick?.(transitionChapter);
                    }
                  }}
                  onToggle={(enabled) => {
                    if (!enabled && isTransitionSelected) {
                      // Switch away via handleChapterCardClick so pending edits on this transition —
                      // are saved before we navigate off it.
                      handleChapterCardClick?.(chapterChapter);
                    }
                    setChapterTransitionVisibility(section.id, enabled).catch(
                      (e) => console.error("Error toggling transition", e)
                    );
                  }}
                  thumbnail={
                    isFlashProject &&
                    transitionSlide &&
                    transitionSlideTemplate ? (
                      <SlideTemplatePreview
                        slide={transitionSlide}
                        template={transitionSlideTemplate}
                        opacity={1}
                        templateSize={thumbSize}
                        border={THUMBNAIL_BORDER}
                        onCardClick={() =>
                          handleSlideCardClick?.(transitionSlide.uuid)
                        }
                        marginBlockEnd={0}
                      />
                    ) : (
                      <TemplatePreview
                        chapter={transitionChapter}
                        opacity={1}
                        templateSize={thumbSize}
                        previewText={section.topic}
                        border={THUMBNAIL_BORDER}
                        onCardClick={() =>
                          handleChapterCardClick?.(transitionChapter)
                        }
                      />
                    )
                  }
                />

                {isFlashProject ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={(event) => {
                      const { active, over } = event;
                      if (!over || active.id === over.id) return;
                      const uuids = sceneSlides.map((s) => s.uuid);
                      const from = uuids.indexOf(String(active.id));
                      const to = uuids.indexOf(String(over.id));
                      if (from === -1 || to === -1) return;
                      reorderScenes(
                        section.id,
                        arrayMove(uuids, from, to)
                      ).catch(() => undefined);
                    }}
                  >
                    <SortableContext
                      items={sceneSlides.map((s) => s.uuid)}
                      strategy={verticalListSortingStrategy}
                    >
                      {sceneSlides.map((slide, sceneIndex) => {
                        const template = templates.get(slide.uuid);
                        if (!template) return null;
                        const selected = currentSlide === slide.uuid;
                        return (
                          <SortableSceneRow
                            key={slide.uuid}
                            id={slide.uuid}
                            label={t(`${SLIDES_I18N}.scene`, {
                              number: sceneIndex + 1,
                            })}
                            selected={selected}
                            onSelect={() => handleSlideCardClick?.(slide.uuid)}
                            onDelete={
                              // Never allow deleting the first scene of a
                              // chapter — it anchors the chapter's narration.
                              canRemove && sceneIndex > 0
                                ? () => {
                                    removeSlide(slide.uuid).catch(noop);
                                  }
                                : undefined
                            }
                            thumbnail={
                              <SlideTemplatePreview
                                slide={slide}
                                template={template}
                                opacity={1}
                                templateSize={thumbSize}
                                border={THUMBNAIL_BORDER}
                                onCardClick={() =>
                                  handleSlideCardClick?.(slide.uuid)
                                }
                                marginBlockEnd={0}
                              />
                            }
                          />
                        );
                      })}
                    </SortableContext>
                  </DndContext>
                ) : (
                  renderStandaloneScene(
                    chapterChapter,
                    t(`${SLIDES_I18N}.scene`, { number: chapterIndex + 1 }),
                    currentChapter.type === TemplateType.CHAPTER &&
                      currentChapter.sectionId === section.id
                  )
                )}

                {isFlashProject && lastSceneUuid && (
                  <ScenePickerPopover
                    onPick={(layout) => handlePick(lastSceneUuid, layout)}
                  >
                    <Button
                      type="dashed"
                      size="small"
                      icon={<PlusIcon size={14} />}
                      style={{ alignSelf: "center", marginBlock: 8 }}
                    >
                      {t(`${SLIDES_I18N}.addScene`)}
                    </Button>
                  </ScenePickerPopover>
                )}
              </SortableChapter>
            );
          })}
        </SortableContext>
      </DndContext>

      {closing && <Divider style={{ margin: 0 }} />}
      {closing &&
        renderToggleableScene(
          closingChapter,
          t(`${SLIDES_I18N}.closingScene`),
          closingEnabled
            ? t(`${I18N}.disableClosing`)
            : t(`${I18N}.enableClosing`),
          closingEnabled,
          currentChapter.type === TemplateType.CLOSING,
          (enabled) => {
            setIntroClosingEnabled(ENABLE_CLOSING_KEY, enabled).catch((e) =>
              console.error("Error toggling closing", e)
            );
          }
        )}

      <MainImageSelectModal
        open={!!pendingPick}
        onCancel={() => setPendingPick(null)}
        sectionId={
          storyboardSections.find((section) =>
            section.slides.some((slide) => slide.uuid === pendingPick?.anchorUuid)
          )?.id
        }
        onSelect={(assetId, path, mediaType, script) => {
          if (!pendingPick) return;
          const { anchorUuid, layout } = pendingPick;
          setPendingPick(null);
          addSlide(
            anchorUuid,
            layout,
            "after",
            { assetId, path, mediaType },
            script
          ).catch(() => undefined);
        }}
      />
    </Flex>
  );
};
